import { useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  getReactSelectValue,
  ReactSelectInput,
} from '@hivebrite/design-system/Inputs';

import { VIDEO_ARRANGEMENT_OPTIONS } from 'hb-builder/components/BuilderComponents/block/Hero/utils/constants';
import CheckboxSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/CheckboxSetting';
import DeleteImageSource from 'hb-builder/components/common/SettingsLibrary/CommonSettings/DeleteImageSource';
import UploadVideoButton from 'hb-builder/components/common/SettingsLibrary/CommonSettings/UploadVideoButton';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import { positionOptions } from 'hb-builder/constants/imageArrangement';
import type { OnChangeSetting } from 'hb-builder/types/settings';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { HeroItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/heroItemAttributes';

interface VideoArrangementProps {
  canHeroBeFullWidth: boolean;
  item: DocumentItem<HeroItemAttributes>;
  onChange: OnChangeSetting;
}

const VideoArrangement = ({
  canHeroBeFullWidth,
  item,
  onChange,
}: VideoArrangementProps) => {
  const { attributes } = item;

  const [arrangementValue, setArrangementValue] = useState(
    attributes?.videoBackgroundArrangement || 'fit',
  );
  const [positionValue, setPositionValue] = useState(
    attributes?.videoBackgroundPosition || 'center',
  );

  const settingProps = { item, onChange };
  const hasVideo = !!attributes?.containerBackgroundVideo;

  const handleChangeArrangement = ({ value }) => {
    setArrangementValue(value);

    onChange({
      id: item.id,
      settingKey: 'attributes.videoBackgroundArrangement',
      value,
    });
  };

  const handleChangePosition = (e) => {
    setPositionValue(e.value);

    onChange({
      id: item.id,
      settingKey: 'attributes.videoBackgroundPosition',
      value: e.value,
    });
  };

  const valueSelected = getReactSelectValue(
    VIDEO_ARRANGEMENT_OPTIONS,
    arrangementValue,
  );

  const positionValueSelected = getReactSelectValue(
    positionOptions,
    positionValue,
  );

  const reactSelectProps = {
    ariaDescribedBy: undefined,
    ariaLabel: undefined,
    cacheOptions: undefined,
    defaultOptions: undefined,
    isLoading: undefined,
    isMulti: undefined,
    isSearchable: undefined,
    label: undefined,
    loadOptions: undefined,
    menuPortalTarget: undefined,
    menuPosition: undefined,
    noOptionsMessage: undefined,
    selectComponent: undefined,
  };

  return (
    <>
      <UploadVideoButton
        item={item}
        hasVideo={hasVideo}
        attributeType="containerBackgroundVideo"
      />
      <Box mt={2} />
      <DeleteImageSource
        settingKey="attributes.containerBackgroundVideo"
        {...settingProps}
        content={s__('Builder|Delete video')}
      />

      <VideoSettingWrapper mt={1}>
        <InputTitle
          id="heroSettingsVideoArrangement"
          label={__('Video arrangement')}
        />
        <ReactSelectInput
          id="heroSettingsVideoArrangement"
          name="heroSettingsVideoArrangement"
          onChange={handleChangeArrangement}
          options={VIDEO_ARRANGEMENT_OPTIONS}
          value={valueSelected}
          variant="small"
          width={133}
          {...reactSelectProps}
        />
      </VideoSettingWrapper>
      <VideoSettingWrapper>
        <InputTitle
          id="heroSettingsVideoPosition"
          label={__('Video position')}
        />
        <ReactSelectInput
          id="heroSettingsImagePosition"
          name="heroSettingsImagePosition"
          onChange={handleChangePosition}
          options={positionOptions}
          value={positionValueSelected}
          variant="small"
          width={133}
          {...reactSelectProps}
        />
      </VideoSettingWrapper>

      <CheckboxSetting
        settingKey="darkenedVideo"
        title={__('Darken video')}
        {...settingProps}
      />
      {canHeroBeFullWidth && (
        <CheckboxSetting
          settingKey="fullWidth"
          title={__('Full width background')}
          {...settingProps}
        />
      )}
    </>
  );
};
export default VideoArrangement;

const VideoSettingWrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 4,
}))``;
